import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Paper, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
  Select, MenuItem, FormControl, InputLabel, Checkbox, FormGroup,
  FormControlLabel, IconButton, Typography, Stack, Chip,
  Card, CardContent, CardActionArea, Box, Divider, Collapse,
  Table, TableHead, TableRow, TableCell, TableBody, Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const API_BASE = 'https://portal.peaksoftcon.com:8081/api';

// Onboarding checklist — every candidate firm walks through these 13 steps.
// All must be ticked before the "Firma Takibe Aktar" button unlocks.
const CHECKLIST_FIELDS = [
  { key: 'taslak_gonderildi',      label: 'Taslak Gönderildi' },
  { key: 'bilgi_formu_iletildi',   label: 'Bilgi Formu İletildi' },
  { key: 'degerlendirme_basladi',  label: 'Değerlendirme Başladı' },
  { key: 'yk_karari',              label: 'YK Kararı' },
  { key: 'sirket_kurulumu',        label: 'Şirket Kurulumu' },
  { key: 'teminat',                label: 'Teminat' },
  { key: 'kira_sozlesmesi',        label: 'Kira Sözleşmesi' },
  { key: 'proje_onay_yazilari',    label: 'Proje Onay Yazıları' },
  { key: 'kdv_muafiyet_kontrol',   label: 'KDV Muafiyet Kontrol' },
  { key: 'sgkya_iletildi',         label: "SGK'ya İletildi" },
  { key: 'firma_karti_dolduruldu', label: 'Firma Kartı Dolduruldu' },
  { key: 'personel_portal_girisi', label: 'Personel Portal Girişi' },
  { key: 'isyeri_acma_ruhsati',    label: 'İşyeri Açma Ruhsatı' },
];

const FILE_TITLES = [
  'Proje Onay Yazısı',
  'Kira Sözleşmesi',
  'İşe Giriş Evrakları',
];

function AdminAspiringTracking() {
  const [rows, setRows] = useState([]);
  const [notesDraft, setNotesDraft] = useState({});
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Progress = how many of the 13 onboarding steps are ticked. Cards are
  // sorted ascending (least progress first); a left-border colour encodes
  // the bucket so the admin scans the list visually.
  const computeProgress = useCallback((row) => {
    const total = CHECKLIST_FIELDS.length;
    const done = CHECKLIST_FIELDS.filter((f) => !!row[f.key]).length;
    const ratio = total > 0 ? done / total : 0;
    let bucket; // 'error' | 'warning' | 'info' | 'success'
    if (ratio === 1) bucket = 'success';
    else if (ratio >= 0.5) bucket = 'info';
    else if (ratio > 0) bucket = 'warning';
    else bucket = 'error';
    return { done, total, ratio, bucket };
  }, []);

  const sortedRows = useMemo(() => {
    return [...rows]
      .map((r) => ({ row: r, p: computeProgress(r) }))
      .sort((a, b) => {
        if (a.p.ratio !== b.p.ratio) return a.p.ratio - b.p.ratio;
        return (a.row.company_name || '').localeCompare(b.row.company_name || '', 'tr');
      })
      .map((x) => x.row);
  }, [rows, computeProgress]);

  // ── Add-candidate dialog state ──
  const [addOpen, setAddOpen] = useState(false);
  const emptyForm = {
    company_name: '', company_password: '', company_fullname: '',
    company_mail: '', company_founddate: '',
  };
  const [addForm, setAddForm] = useState(emptyForm);

  // ── Upload / files dialog state ──
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState(FILE_TITLES[0]);

  const [filesOpen, setFilesOpen] = useState(false);
  const [filesTarget, setFilesTarget] = useState(null);
  const [filesList, setFilesList] = useState([]);

  // ── Transfer dialog state ──
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferTarget, setTransferTarget] = useState(null);
  const [transferForm, setTransferForm] = useState({
    company_password: '', role: 'Client', monthly_fee: '', billing_day: 1,
  });
  const [transferBusy, setTransferBusy] = useState(false);
  const [transferError, setTransferError] = useState('');

  const fetchRows = useCallback(() => {
    axios
      .get(`${API_BASE}/candidateCompanies`)
      .then((res) => {
        setRows(res.data || []);
        const draft = {};
        (res.data || []).forEach((r) => { draft[r.id] = r.notlar || ''; });
        setNotesDraft(draft);
      })
      .catch((err) => console.error('Error fetching candidates:', err));
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const updateRow = (id, patch) => {
    axios
      .put(`${API_BASE}/candidateCompanies/${id}`, patch)
      .then(() => {
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
      })
      .catch((err) => console.error('Error updating candidate:', err));
  };

  const handleStatusToggle = (row, key) => {
    updateRow(row.id, { [key]: row[key] ? 0 : 1 });
  };

  const handleNotesBlur = (row) => {
    const next = notesDraft[row.id] ?? '';
    if ((row.notlar || '') === next) return;
    updateRow(row.id, { notlar: next });
  };

  // ── Add candidate ──
  const openAdd = () => {
    setAddForm(emptyForm);
    setAddOpen(true);
  };

  const submitAdd = async () => {
    if (!addForm.company_name.trim()) return;
    try {
      await axios.post(`${API_BASE}/candidateCompanies`, addForm);
      setAddOpen(false);
      fetchRows();
    } catch (err) {
      console.error('Error adding candidate:', err);
    }
  };

  // ── Files ──
  const openUpload = (row) => {
    setUploadTarget(row);
    setUploadFile(null);
    setUploadTitle(FILE_TITLES[0]);
    setUploadOpen(true);
  };

  const submitUpload = async () => {
    if (!uploadFile || !uploadTarget) return;
    const fd = new FormData();
    fd.append('file', uploadFile);
    fd.append('baslik', uploadTitle);
    try {
      await axios.post(
        `${API_BASE}/candidateCompanies/${uploadTarget.id}/files`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setUploadOpen(false);
      fetchRows();
    } catch (err) {
      console.error('Error uploading file:', err);
    }
  };

  const openFiles = (row) => {
    setFilesTarget(row);
    setFilesOpen(true);
    axios
      .get(`${API_BASE}/candidateCompanies/${row.id}/files`)
      .then((res) => setFilesList(res.data || []))
      .catch((err) => console.error('Error fetching files:', err));
  };

  const deleteFile = (fileId) => {
    if (!window.confirm('Bu dosyayı silmek istediğinize emin misiniz?')) return;
    axios
      .delete(`${API_BASE}/candidateCompanies/files/${fileId}`)
      .then(() => {
        setFilesList((prev) => prev.filter((f) => f.id !== fileId));
        fetchRows();
      })
      .catch((err) => console.error('Error deleting file:', err));
  };

  const downloadFile = (fileId, fileName) => {
    const url = `${API_BASE}/candidateCompanies/files/${fileId}/download`;
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || '';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const deleteCandidate = (row) => {
    if (!window.confirm(`"${row.company_name}" aday firmasını silmek istediğinize emin misiniz?`)) return;
    axios
      .delete(`${API_BASE}/candidateCompanies/${row.id}`)
      .then(() => setRows((prev) => prev.filter((r) => r.id !== row.id)))
      .catch((err) => console.error('Error deleting candidate:', err));
  };

  // ── Transfer to Firma Takibi ──
  const openTransfer = (row) => {
    setTransferTarget(row);
    setTransferForm({ company_password: '', role: 'Client', monthly_fee: '', billing_day: 1 });
    setTransferError('');
    setTransferOpen(true);
  };

  const submitTransfer = async () => {
    if (!transferTarget) return;
    setTransferBusy(true);
    setTransferError('');
    try {
      await axios.post(
        `${API_BASE}/candidateCompanies/${transferTarget.id}/transfer`,
        transferForm,
      );
      setTransferOpen(false);
      setRows((prev) => prev.filter((r) => r.id !== transferTarget.id));
    } catch (err) {
      console.error('Error transferring candidate:', err);
      setTransferError(err.response?.data?.error || 'Aktarım sırasında bir hata oluştu.');
    } finally {
      setTransferBusy(false);
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mb: 2 }}
      >
        <Typography variant="h4" sx={{ flexGrow: 1 }}>Aday Firma Takibi</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Toplam: {rows.length} aday
        </Typography>
        <Button variant="contained" color="primary" onClick={openAdd}>
          + Yeni Aday Firma
        </Button>
      </Stack>

      <Stack spacing={1.5}>
        {sortedRows.length === 0 && (
          <Paper sx={{ py: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">Henüz aday firma yok.</Typography>
          </Paper>
        )}
        {sortedRows.map((row) => {
          const expanded = expandedIds.has(row.id);
          const p = computeProgress(row);
          const isReady = p.ratio === 1;
          return (
            <Card
              key={row.id}
              sx={{
                bgcolor: 'background.paper',
                borderLeft: 5,
                borderLeftColor: `${p.bucket}.main`,
                transition: 'background-color 0.2s, border-color 0.2s',
              }}
            >
              <CardActionArea onClick={() => toggleExpand(row.id)} component="div">
                <CardContent sx={{ py: 1.5 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={{ xs: 1, sm: 2 }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {isReady && <CheckCircleIcon fontSize="small" color="success" />}
                        <Typography variant="h6" sx={{ wordBreak: 'break-word' }}>
                          {row.company_name}
                        </Typography>
                      </Stack>
                      {row.company_fullname && (
                        <Typography variant="caption" color="text.secondary">
                          {row.company_fullname}
                        </Typography>
                      )}
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                      <Chip size="small" label={`${p.done}/${p.total}`} color={p.bucket} />
                      {row.file_count ? (
                        <Chip label={`${row.file_count} dosya`} size="small" variant="outlined" />
                      ) : null}
                      <IconButton
                        size="small"
                        sx={{
                          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s',
                        }}
                      >
                        <ExpandMoreIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </CardContent>
              </CardActionArea>

              <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Divider />
                <CardContent>
                  <Typography variant="overline" color="text.secondary">Aday Durumu</Typography>
                  <FormGroup sx={{ mb: 1 }}>
                    {CHECKLIST_FIELDS.map((sf) => (
                      <FormControlLabel
                        key={sf.key}
                        control={
                          <Checkbox
                            size="small"
                            checked={!!row[sf.key]}
                            onChange={() => handleStatusToggle(row, sf.key)}
                          />
                        }
                        label={<Typography variant="body2">{sf.label}</Typography>}
                      />
                    ))}
                  </FormGroup>

                  <Divider sx={{ my: 1 }} />
                  <Typography variant="overline" color="text.secondary">Notlar</Typography>
                  <TextField
                    multiline
                    minRows={3}
                    maxRows={10}
                    fullWidth
                    size="small"
                    value={notesDraft[row.id] ?? ''}
                    onChange={(e) =>
                      setNotesDraft((prev) => ({ ...prev, [row.id]: e.target.value }))
                    }
                    onBlur={() => handleNotesBlur(row)}
                    placeholder="Aday firma ile ilgili notlar..."
                    sx={{ mt: 1 }}
                  />

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
                    <Button variant="contained" size="small" onClick={() => openUpload(row)}>
                      Dosya Yükle
                    </Button>
                    <Button variant="outlined" size="small" onClick={() => openFiles(row)}>
                      Dosyalar{row.file_count ? ` (${row.file_count})` : ''}
                    </Button>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button variant="outlined" size="small" color="error" onClick={() => deleteCandidate(row)}>
                      Adayı Sil
                    </Button>
                  </Stack>

                  <Divider sx={{ my: 2 }} />
                  <Stack spacing={1}>
                    {!isReady && (
                      <Typography variant="caption" color="text.secondary">
                        Aktarmak için tüm adımlar tamamlanmalı ({p.done}/{p.total}).
                      </Typography>
                    )}
                    <Button
                      variant="contained"
                      color="success"
                      fullWidth
                      disabled={!isReady}
                      onClick={() => openTransfer(row)}
                    >
                      Firma Takibe Aktar
                    </Button>
                  </Stack>
                </CardContent>
              </Collapse>
            </Card>
          );
        })}
      </Stack>

      {/* ── Add candidate dialog ── */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Yeni Aday Firma</DialogTitle>
        <DialogContent>
          {[
            { name: 'company_name',      label: 'Firma İsmi (Kullanıcı Adı)' },
            { name: 'company_password',  label: 'Şifre', type: 'password' },
            { name: 'company_fullname',  label: 'Firma Ünvanı' },
            { name: 'company_mail',      label: 'E-posta', type: 'email' },
            { name: 'company_founddate', label: 'Kuruluş Tarihi', type: 'date' },
          ].map((f) => (
            <TextField
              key={f.name}
              label={f.label}
              name={f.name}
              type={f.type || 'text'}
              value={addForm[f.name] || ''}
              onChange={(e) => setAddForm((prev) => ({ ...prev, [f.name]: e.target.value }))}
              fullWidth
              margin="dense"
              InputLabelProps={f.type === 'date' ? { shrink: true } : undefined}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>İptal</Button>
          <Button onClick={submitAdd} variant="contained" disabled={!addForm.company_name.trim()}>
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Upload dialog ── */}
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Dosya Yükle
          {uploadTarget && (
            <Typography variant="body2" color="text.secondary">
              {uploadTarget.company_name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>Başlık</InputLabel>
            <Select
              label="Başlık"
              value={uploadTitle}
              onChange={(e) => setUploadTitle(e.target.value)}
            >
              {FILE_TITLES.map((t) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <input
            type="file"
            onChange={(e) => setUploadFile(e.target.files[0] || null)}
            style={{ marginTop: 16, display: 'block' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)}>İptal</Button>
          <Button onClick={submitUpload} variant="contained" disabled={!uploadFile}>
            Yükle
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Files list dialog ── */}
      <Dialog open={filesOpen} onClose={() => setFilesOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Dosyalar
          {filesTarget && (
            <Typography variant="body2" color="text.secondary">
              {filesTarget.company_name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Başlık</TableCell>
                <TableCell>Dosya</TableCell>
                <TableCell>Boyut</TableCell>
                <TableCell>Tarih</TableCell>
                <TableCell align="right">İşlem</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filesList.map((f) => (
                <TableRow key={f.id}>
                  <TableCell>{f.baslik}</TableCell>
                  <TableCell>{f.file_name}</TableCell>
                  <TableCell>{f.size_bytes ? `${Math.round(f.size_bytes / 1024)} KB` : '-'}</TableCell>
                  <TableCell>
                    {f.uploaded_at ? new Date(f.uploaded_at).toLocaleString('tr-TR') : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => downloadFile(f.id, f.file_name)}>
                      İndir
                    </Button>
                    <Button size="small" color="error" onClick={() => deleteFile(f.id)}>
                      Sil
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {filesList.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>
                    Henüz dosya yüklenmemiş.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilesOpen(false)}>Kapat</Button>
        </DialogActions>
      </Dialog>

      {/* ── Transfer dialog ── */}
      <Dialog open={transferOpen} onClose={() => !transferBusy && setTransferOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Firma Takibe Aktar
          {transferTarget && (
            <Typography variant="body2" color="text.secondary">
              {transferTarget.company_name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Bu aday firma kalıcı firma listesine eklenecek, yüklenen evraklar bu ayın Firma
            Takibi kaydına taşınacak ve aday listesinden kaldırılacaktır.
          </Typography>
          {transferError && <Alert severity="error" sx={{ mb: 1 }}>{transferError}</Alert>}
          <TextField
            label="Şifre (boş bırakılırsa aday şifresi kullanılır)"
            type="password"
            value={transferForm.company_password}
            onChange={(e) => setTransferForm((p) => ({ ...p, company_password: e.target.value }))}
            fullWidth
            margin="dense"
          />
          <FormControl fullWidth margin="dense">
            <InputLabel id="transfer-role-label">Rol</InputLabel>
            <Select
              labelId="transfer-role-label"
              label="Rol"
              value={transferForm.role}
              onChange={(e) => setTransferForm((p) => ({ ...p, role: e.target.value }))}
            >
              <MenuItem value="Client">Client</MenuItem>
              <MenuItem value="Admin">Admin</MenuItem>
            </Select>
          </FormControl>
          {transferForm.role !== 'Admin' && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Aylık Tutar (₺)"
                type="number"
                inputProps={{ step: '0.01', min: 0 }}
                value={transferForm.monthly_fee}
                onChange={(e) => setTransferForm((p) => ({ ...p, monthly_fee: e.target.value }))}
                fullWidth
                margin="dense"
              />
              <FormControl fullWidth margin="dense">
                <InputLabel id="transfer-billing-label">Kesim Günü</InputLabel>
                <Select
                  labelId="transfer-billing-label"
                  label="Kesim Günü"
                  value={transferForm.billing_day}
                  onChange={(e) => setTransferForm((p) => ({ ...p, billing_day: e.target.value }))}
                >
                  <MenuItem value={1}>Ayın 1'i</MenuItem>
                  <MenuItem value={15}>Ayın 15'i</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferOpen(false)} disabled={transferBusy}>İptal</Button>
          <Button onClick={submitTransfer} variant="contained" color="success" disabled={transferBusy}>
            {transferBusy ? 'Aktarılıyor...' : 'Aktar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminAspiringTracking;
